<template>
  <div
    v-if="!footerEmpty && layoutClass !== 'mobile-leaderboard'"
    :class="[classes.footer, 'footer', layoutClass]"
    ref="footerRef"
  >
    <div
      v-if="displayStatic"
      :class="[
        classes.fixedText,
        useScrollingText ? 'withScrolling' : 'withOutScrolling',
        layoutClass
      ]"
    >
      <BrandText
        tag="span"
        styleGroup="Terms"
        :overideText="!useScrollingText && overideTerms"
        :get="getStatic"
        :truncate="truncateSize"
        :enableTextEditing="false"
        :class="[{ [classes.emptyText]: footerEmpty }]"
      />
    </div>
    <div v-if="useScrollingText" :class="[classes.marquee, layoutClass]">
      <BrandMarquee
        :animationTime="animationTime"
        tag="span"
        styleGroup="Terms"
        :overideText="overideTerms"
        :get="getScrolling"
        :enableTextEditing="false"
        :class="[{ [classes.emptyText]: footerEmpty }]"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import BrandText from "./BrandText.vue";
import BrandMarquee from "./BrandMarquee.vue";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { useGetOffer, useShouldDisplayOffer } from "../hooks/useDisplayOffer";
import { Brand } from "@fresh8/types/src";
import {
  injectTemplateSize,
  SMALL_RESPONSIVE,
  LEADERBOARD,
  LARGE_LEADERBOARD,
  MOBILE_LEADERBOARD,
  LMLB,
  MPU
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { camelize } from "@fresh8/ad-data-composition/src/lib/formatText";

const useStyles = createUseStyles({
  footer: {
    display: "flex",
    lineHeight: 1,
    fontSize: "9px",
    position: "relative",
    textAlign: "center",

    [`&.${camelize(SMALL_RESPONSIVE.name)}`]: {
      fontSize: "7px"
    },

    [`&.${camelize(MOBILE_LEADERBOARD.name)}`]: {
      height: "10px",
      fontSize: "6px",
      border: "none",
      borderTop: props =>
        !props.displayStatic && !props.useScrollingText
          ? "none"
          : `1px solid ${props.marketBorderColor}`,

      marginTop: "2px",
      padding: "0px"
    },
    [`&.${camelize(LMLB.name)}`]: {
      height: "10px",
      fontSize: "6px",
      border: "none",
      padding: "0px"
    },
    [`&.${LEADERBOARD.name}`]: {
      height: "10px",
      fontSize: "6px"
    },
    [`&.${camelize(LARGE_LEADERBOARD.name)}`]: {
      height: "10px",
      fontSize: "6px"
    },
    [`&.${camelize(MPU.name)}`]: {
      paddingTop: "2px"
    }
  },
  emptyText: {
    display: "block",
    width: "100%",
    minHeight: "12px"
  },
  fixedText: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    "&.withScrolling": {
      flex: "none",
      borderRight: props => `1px solid ${props.marketBorderColor}`,
      padding: "3px 4px 2px 0",
      whiteSpace: "nowrap"
    },
    [`&.withScrolling.${camelize(MOBILE_LEADERBOARD.name)}`]: {
      boxShadow: "none",
      borderRight: props => `1px solid ${props.marketBorderColor}`,
      padding: "0 5px"
    },
    [`&.withScrolling.${camelize(LMLB.name)}`]: {
      boxShadow: "none",
      borderRight: props => `1px solid ${props.marketBorderColor}`,
      padding: "0 5px"
    },
    "&.withOutScrolling": {
      justifyContent: "center",
      padding: "5px 0 0",
      [`&.${camelize(SMALL_RESPONSIVE.name)}, &.${LEADERBOARD.name} &,`]: {
        padding: "3px 0 2px"
      },

      [`&.${camelize(MPU.name)}`]: {
        padding: "0"
      },
      [`&.${camelize(MOBILE_LEADERBOARD.name)}, &${camelize(LMLB.name)}`]: {
        padding: "5px 0 0",
        "& span": {
          padding: "0",
          height: "10px"
        }
      }
    }
  },

  marquee: {
    overflow: "hidden",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "3px 0 2px",
    [`&.${camelize(MOBILE_LEADERBOARD.name)}`]: {
      padding: "0px",
      "& span": {
        padding: "2px"
      }
    },
    [`&.${camelize(SMALL_RESPONSIVE.name)}`]: {
      paddingTop: "2px"
    }
  }
});

export default defineComponent({
  name: "Footer",
  components: {
    BrandText,
    BrandMarquee
  },
  setup() {
    const footerRef = ref<HTMLDivElement>(null);

    const templateContext = injectTemplateSize();
    const { footerBranding, adTemplateBranding, brand } = injectBrand();

    const offer = useGetOffer();
    const showOffer = useShouldDisplayOffer();
    const overideTerms = computed(() => showOffer.value && offer.value?.terms);
    const truncateSize = computed(() => {
      const truncateSchema = {
        [MOBILE_LEADERBOARD.name]: 1,
        [LMLB.name]: 1,
        [LEADERBOARD.name]: 1,
        [LARGE_LEADERBOARD.name]: 1,
        [SMALL_RESPONSIVE.name]: 2
      };
      return truncateSchema[templateContext.size.value.name] || 3;
    });

    // computing the useScrolling text (if shows the marquee component or not the default static text)
    const useScrollingText = computed(() => {
      const b = brand.value as Brand;
      return (
        b.adTemplate.footer.useScrollingText &&
        (b.adTemplate.footer.scrolling.value.length > 0 ||
          overideTerms.value?.length > 0)
      );
    });

    const footerEmpty = computed(
      () =>
        !footerBranding.value?.static?.value?.length &&
        !footerBranding.value?.value?.length &&
        !footerBranding.value?.scrolling?.value?.length
    );

    const displayStatic = computed(() => {
      return (
        !!footerBranding.value?.static?.value ||
        (!!footerBranding.value?.value &&
          typeof footerBranding.value?.value === "string")
      );
    });

    const animationTime = computed(() => footerBranding.value?.animationTime);

    const computedStyles = computed(() => ({
      ...adTemplateBranding.value,
      displayStatic: displayStatic.value,
      displayMarquee: useScrollingText.value
    }));

    const classesRef = useStyles(computedStyles);

    return {
      animationTime,
      footerEmpty,
      overideTerms,
      classes: classesRef,
      layoutClass: computed(() => camelize(templateContext.size.value.name)),
      getScrolling: (brand: Brand) => brand.adTemplate.footer.scrolling,
      getStatic: (brand: Brand) =>
        brand.adTemplate.footer.static || brand.adTemplate.footer,
      displayStatic,
      truncateSize,
      useScrollingText,
      footerRef
    };
  }
});
</script>
